import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Radar } from "lucide-react";
import ChapterHeader from "./ChapterHeader";
import ImportanceBadge from "./intel/ImportanceBadge";
import { intelApi, fmtDate } from "../lib/intelApi";

// Each claim below is verified against /app/backend/intel/* — keep copy in sync with the implementation.
const HOW_BUILT = [
  { label: "Source adapters", detail: "Nine providers behind one adapter interface — RSS, Atom and JSON Feed" },
  { label: "Ingestion", detail: "Scheduled every 6 hours, conditional GET (ETag / Last-Modified), deduplicated" },
  { label: "Enrichment", detail: "AI summary, impact assessment and recommended action per item" },
  { label: "Scoring", detail: "AI-scored relevance and severity, ranked by a fixed weighting tuned to my technology profile" },
  { label: "Delivery", detail: "Cached read path — no external fetch at page render" },
];

export default function IntelTeaser() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = document.getElementById("intelligence");
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          intelApi.teaser().then(setData).catch(() => setFailed(true));
          io.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="intelligence" data-testid="intel-teaser-section" className="relative border-t border-border py-24 md:py-40 bg-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <ChapterHeader
          n="06"
          kicker="Signal feed"
          title={<>Tech<br /><span className="text-accent">Intelligence</span>.</>}
          titleClassName="md:text-4xl xl:text-5xl"
        >
          <div className="space-y-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <p className="text-lg md:text-xl leading-relaxed text-foreground/85 max-w-[52ch]" data-testid="intel-teaser-intro">
                A production intelligence pipeline I built and run. It ingests official release and security feeds from nine
                cloud and DevOps sources, deduplicates and enriches each item with AI, scores it against my technology profile,
                and surfaces only what warrants action.
              </p>
              <Link
                to="/intelligence"
                data-testid="intel-teaser-cta"
                className="shrink-0 inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors"
              >
                Open dashboard <ArrowRight size={14} />
              </Link>
            </div>

            <dl className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border" data-testid="intel-how-built">
              {HOW_BUILT.map((item, i) => (
                <div key={item.label} className={`bg-background p-4 md:p-5 ${i === 4 ? "col-span-2 md:col-span-1" : ""}`}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent">{item.label}</dt>
                  <dd className="mt-2 font-mono text-[11px] leading-relaxed text-foreground/75">{item.detail}</dd>
                </div>
              ))}
            </dl>

            {failed ? (
              <div className="border border-border p-8 text-sm text-muted-foreground font-mono uppercase tracking-[0.15em]">
                Feed temporarily unavailable.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border" data-testid="intel-teaser-grid">
                {(data ? data.items : [null, null, null]).map((item, i) => (
                  <motion.a
                    key={item?.id || i}
                    href={item?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="group bg-background p-6 md:p-7 flex flex-col gap-4 hover:bg-surface transition-colors min-h-[220px]"
                    data-testid={`intel-teaser-card-${i}`}
                  >
                    {item ? (
                      <>
                        <div className="flex items-center gap-3">
                          <ImportanceBadge level={item.importance} />
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                            {item.technology} · {fmtDate(item.published_at)}
                          </span>
                        </div>
                        <h3 className="font-heading font-black text-lg md:text-xl tracking-tight leading-tight group-hover:text-accent transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-sm text-foreground/70 leading-relaxed line-clamp-3">{item.ai_summary}</p>
                        <span className="mt-auto pt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-accent">
                          Source <ArrowUpRight size={12} />
                        </span>
                      </>
                    ) : (
                      <div className="animate-pulse space-y-3">
                        <div className="h-4 w-24 bg-muted" />
                        <div className="h-6 w-4/5 bg-muted" />
                        <div className="h-3 w-full bg-muted" />
                        <div className="h-3 w-2/3 bg-muted" />
                      </div>
                    )}
                  </motion.a>
                ))}
              </div>
            )}

            {data && (
              <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="intel-teaser-count">
                <Radar size={12} className="text-accent" /> {data.week_total} updates tracked in the last 10 days
              </div>
            )}
          </div>
        </ChapterHeader>
      </div>
    </section>
  );
}
